import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../config/api';

interface BizInfo {
  name: string;
  slug: string;
  timezone: string;
  intro: string | null;
  logoUrl: string | null;
  minNoticeHours: number;
  minNoticeMinutes: number;
  allowSameDayBooking: boolean;
  earliestBookable: string;
  maxAdvanceDays: number;
  slotDurationMinutes: number;
}

interface TypeInfo {
  type_id: string;
  name: string;
  color: string;
  icon: string | null;
  default_duration_minutes: number;
}

interface Slot {
  start: string;
  end: string;
}

const STEPS = ['Choose type', 'Pick a time', 'Your details'];

const PublicBookingPage: React.FC = () => {
  const { businessSlug } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [biz, setBiz] = useState<BizInfo | null>(null);
  const [types, setTypes] = useState<TypeInfo[]>([]);

  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState<TypeInfo | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [honeypot, setHoneypot] = useState(''); // bot trap

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/public/booking/${businessSlug}/info`)
      .then((r) => {
        if (!r.data?.success) throw new Error('Public booking is not available');
        const business = r.data.business as BizInfo;
        setBiz(business);
        setTypes(r.data.types || []);
        const firstBookable = business.earliestBookable
          ? dayjs(business.earliestBookable).startOf('day')
          : (business.allowSameDayBooking !== false
            ? dayjs().startOf('day')
            : dayjs().add(1, 'day').startOf('day'));
        setSelectedDate(firstBookable);
      })
      .catch((e) => setError(e.response?.data?.message || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [businessSlug]);

  const loadSlots = useCallback(async (date: Dayjs, type: TypeInfo) => {
    setSlotsLoading(true);
    setSlots([]);
    try {
      const from = date.startOf('day').toISOString();
      const to = date.endOf('day').toISOString();
      const r = await axios.get(`${API_URL}/api/public/booking/${businessSlug}/availability`, {
        params: { from, to, typeId: type.type_id },
      });
      setSlots(r.data.slots || []);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load availability');
    } finally {
      setSlotsLoading(false);
    }
  }, [businessSlug]);

  useEffect(() => {
    if (step !== 1 || !selectedType || !selectedDate) return;
    loadSlots(selectedDate, selectedType);
  }, [step, selectedDate, selectedType, loadSlots]);

  const handleSubmit = useCallback(async () => {
    if (!selectedSlot || !selectedType) return;
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and email');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const r = await axios.post(`${API_URL}/api/public/booking/${businessSlug}/bookings`, {
        typeId: selectedType.type_id,
        startAt: selectedSlot.start,
        notes,
        hp_extra: honeypot,
        customer: { name: name.trim(), email: email.trim(), phone: phone.trim(), company: company.trim() },
      });
      if (!r.data?.success) throw new Error('Submission failed');
      navigate(`/${businessSlug}/booking/confirmed/${r.data.externalToken}`);
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Submission failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [businessSlug, selectedSlot, selectedType, name, email, phone, company, notes, honeypot, navigate]);

  const minDate = useMemo(() => {
    if (!biz) return dayjs().startOf('day');
    if (biz.earliestBookable) return dayjs(biz.earliestBookable).startOf('day');
    return biz.allowSameDayBooking !== false
      ? dayjs().startOf('day')
      : dayjs().add(1, 'day').startOf('day');
  }, [biz]);
  const maxDate = useMemo(() => {
    if (!biz) return dayjs().add(60, 'day');
    return dayjs().add(biz.maxAdvanceDays, 'day');
  }, [biz]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!biz) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error">{error || 'This page is not available.'}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 2, md: 6 } }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {biz.logoUrl ? (
              <Avatar src={biz.logoUrl} sx={{ width: 64, height: 64, mx: 'auto', mb: 2 }} />
            ) : (
              <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: '#2563eb' }}>
                <EventIcon />
              </Avatar>
            )}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{biz.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {biz.intro || 'Book a time that works for you'}
            </Typography>
          </Box>

          <Card sx={{ borderRadius: 4, boxShadow: '0 10px 40px rgba(15,23,42,0.08)' }}>
            <Box sx={{ p: 3, pb: 0 }}>
              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
              </Stepper>
            </Box>
            <Divider sx={{ mt: 3 }} />

            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

              {step === 0 && (
                <Grid container spacing={2}>
                  {types.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info">There are no booking types available right now.</Alert>
                    </Grid>
                  )}
                  {types.map((t) => (
                    <Grid item xs={12} sm={6} key={t.type_id}>
                      <Card
                        sx={{
                          borderRadius: 3, borderLeft: `4px solid ${t.color}`,
                          transition: 'transform 0.15s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' },
                        }}
                        variant="outlined"
                      >
                        <CardActionArea
                          onClick={() => { setSelectedType(t); setStep(1); }}
                          sx={{ p: 2 }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, color: 'text.secondary' }}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="body2">{t.default_duration_minutes} minutes</Typography>
                          </Stack>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {step === 1 && selectedType && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DateCalendar
                      value={selectedDate}
                      onChange={(v) => { setSelectedDate(v); setSelectedSlot(null); }}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedDate?.format('dddd, D MMMM') || 'Pick a date'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Times shown in {biz.timezone}
                    </Typography>
                    {slotsLoading ? (
                      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : slots.length === 0 ? (
                      <Alert severity="info" icon={<ScheduleIcon />}>
                        No slots available on this day. Try another date.
                      </Alert>
                    ) : (
                      <Grid container spacing={1}>
                        {slots.map((s) => {
                          const isSel = selectedSlot?.start === s.start;
                          return (
                            <Grid item xs={6} key={s.start}>
                              <Button
                                fullWidth
                                variant={isSel ? 'contained' : 'outlined'}
                                onClick={() => setSelectedSlot(s)}
                                sx={{ borderRadius: 2, py: 1.2 }}
                              >
                                {dayjs(s.start).format('HH:mm')}
                              </Button>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {step === 2 && selectedSlot && selectedType && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ width: 10, height: 40, bgcolor: selectedType.color, borderRadius: 1 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedType.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(selectedSlot.start).format('dddd, D MMMM · HH:mm')} – {dayjs(selectedSlot.end).format('HH:mm')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField required label="Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                    <TextField required label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Phone" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                    <TextField label="Company" fullWidth value={company} onChange={(e) => setCompany(e.target.value)} />
                  </Stack>
                  <TextField
                    label="Anything we should know?"
                    multiline rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                  {/* Honeypot field - hidden from real users */}
                  <input
                    type="text" name="hp_extra" tabIndex={-1} autoComplete="off"
                    value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
                  />
                </Stack>
              )}
            </CardContent>

            <Divider />
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                startIcon={<ArrowBackIcon />}
                disabled={step === 0}
                onClick={() => { setStep((s) => Math.max(0, s - 1)); setSelectedSlot(null); }}
              >
                Back
              </Button>
              {step === 1 && (
                <Button
                  variant="contained"
                  disabled={!selectedSlot}
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              )}
              {step === 2 && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? <CircularProgress size={18} /> : 'Confirm booking'}
                </Button>
              )}
            </Box>
          </Card>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            Powered by Check-Now
          </Typography>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default PublicBookingPage;
